import { useState } from "react";
import { useNavigate } from "react-router";
import { runtimeConfig } from "@/shared/config/runtime";
import { useAppDispatch, useAppSelector, useAuth } from "@/shared/hooks";
import { LOGIN_PAGE_ROUTE, DEMO_CREATE_ROUTE, ORGANIZATION_PAGE_ROUTE, PROFILE_PAGE_ROUTE, clearFormDrafts } from "@/shared/utils";
import { useLogoutMutation } from "@/features/auth/api";
import { setLoggingOut } from "@/features/auth/slice";
import { setActiveProject } from "@/features/settings";

export function useProfileMenu() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const user = useAppSelector((state) => state.auth.data?.user ?? null);
  const isLoggingOut = useAppSelector((state) => state.auth.isLoggingOut);
  const [logoutRequest, { isLoading: isLogoutRequestLoading }] = useLogoutMutation();
  const [isOpen, setIsOpen] = useState(false);

  const isLogoutLoading = isLogoutRequestLoading || isLoggingOut;
  const isChief = hasRole("chief");
  const email = user?.email ?? null;
  const displayName =
    user?.user_type === "msb_contractor" ? (user.organization?.name ?? "") : (user?.profile?.full_name ?? "");

  function closeMenu() {
    setIsOpen(false);
  }

  function toggleMenu() {
    setIsOpen((prev) => !prev);
  }

  function goToProfile() {
    closeMenu();
    if (user?.user_type === "expert") return;
    navigate(PROFILE_PAGE_ROUTE);
  }

  function goToOrganization() {
    closeMenu();
    navigate(ORGANIZATION_PAGE_ROUTE);
  }

  async function handleLogout() {
    if (isLogoutLoading) return;
    dispatch(setActiveProject(null));
    dispatch(setLoggingOut(true));
    try {
      await logoutRequest().unwrap();
    } catch {
      void 0;
    } finally {
      clearFormDrafts();
      window.location.href = runtimeConfig.isDemo ? DEMO_CREATE_ROUTE : LOGIN_PAGE_ROUTE;
    }
  }

  return {
    isOpen,
    isChief,
    displayName,
    email,
    isLogoutLoading,
    toggleMenu,
    closeMenu,
    goToProfile,
    goToOrganization,
    handleLogout,
  };
}
